//
// QnsPageLinkButton
//
// Small icon button shown next to the QR-code button on the address page
// header when the address owns a `.qie` domain. Provides the primary
// discovery surface for the /qns/[name] page.
//
// Hidden when:
//   - QNS master switch is off
//   - QNS page feature flag is off
//   - The address has no `.qie` domain registered
//
// Clicking navigates to /qns/<primaryDomain>. Tooltip on hover spells out
// the destination so users know what to expect.
//
import { IconButton, Tooltip, chakra } from '@chakra-ui/react';
import type { LinkProps as NextLinkProps } from 'next/link';
import React from 'react';

import { route } from 'nextjs-routes';

import { getEnvValue } from 'configs/app/utils';
import { useQnsResolution } from 'lib/api/useQnsResolution';
import Skeleton from 'ui/shared/chakra/Skeleton';
import NextLink from 'ui/shared/NextLink';
import QnsIcon from 'ui/shared/qns/QnsIcon';

const QNS_PAGE_ENABLED = getEnvValue('NEXT_PUBLIC_QNS_PAGE_ENABLED') !== 'false';

interface Props {
  className?: string;
  addressHash: string;
  isLoading?: boolean;
}

const QnsPageLinkButton = ({ addressHash, className, isLoading }: Props) => {
  const qnsName = useQnsResolution(addressHash);

  // Address has no .qie name → render nothing. No layout shift either, because
  // we only mount the button when there's something to link to.
  if (!QNS_PAGE_ENABLED || !qnsName) {
    return null;
  }

  if (isLoading) {
    return <Skeleton boxSize={ 8 } borderRadius="md" className={ className } flexShrink={ 0 }/>;
  }

  const href = route({ pathname: '/qns/[name]', query: { name: qnsName } });

  return (
    <Tooltip label={ `View ${ qnsName } on QIE Name Service` }>
      <NextLink href={ href as NextLinkProps['href'] } passHref legacyBehavior>
        <IconButton
          as="a"
          className={ className }
          aria-label={ `View ${ qnsName } on QIE Name Service` }
          variant="outline"
          size="sm"
          pl="6px"
          pr="6px"
          icon={ <QnsIcon boxSize={ 5 }/> }
          flexShrink={ 0 }
        />
      </NextLink>
    </Tooltip>
  );
};

export default React.memo(chakra(QnsPageLinkButton));